// ================================================================
// 邀請連結配對頁面（公開，無需登入）
// ================================================================

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { getInviteInfo, submitInvite } from '../api/invite';
import ReportContent from '../components/ReportContent';

// 13 時辰選項（子時分凌晨/深夜）
const hourOptions = [
  { value: '23_early', label: '子時 (00:01-01:00)' },
  { value: '01', label: '丑時 (01:01-03:00)' },
  { value: '03', label: '寅時 (03:01-05:00)' },
  { value: '05', label: '卯時 (05:01-07:00)' },
  { value: '07', label: '辰時 (07:01-09:00)' },
  { value: '09', label: '巳時 (09:01-11:00)' },
  { value: '11', label: '午時 (11:01-13:00)' },
  { value: '13', label: '未時 (13:01-15:00)' },
  { value: '15', label: '申時 (15:01-17:00)' },
  { value: '17', label: '酉時 (17:01-19:00)' },
  { value: '19', label: '戌時 (19:01-21:00)' },
  { value: '21', label: '亥時 (21:01-23:00)' },
  { value: '23_late', label: '子時 (23:01-24:00)' },
];

const toHourValue = (v: string) => (v === '23_early' || v === '23_late') ? '23' : v;

const buildBirthTime = (year: string, month: string, day: string, hour: string): string => {
  let y = parseInt(year, 10);
  let m = parseInt(month, 10);
  let d = parseInt(day, 10);
  if (hour === '23_early') {
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() - 1);
    y = date.getFullYear();
    m = date.getMonth() + 1;
    d = date.getDate();
  }
  return `${y}${m.toString().padStart(2, '0')}${d.toString().padStart(2, '0')}${toHourValue(hour)}`;
};

const InvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteStatus, setInviteStatus] = useState<'pending' | 'completed' | 'expired' | null>(null);
  const [questionText, setQuestionText] = useState('');
  const [questionId, setQuestionId] = useState('');
  const [initiatorName, setInitiatorName] = useState('');
  const [result, setResult] = useState('');

  // 表單欄位
  const [name, setName] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [hour, setHour] = useState('11');
  const [gender, setGender] = useState<'Male' | 'Female'>('Male');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!token) return;
    getInviteInfo(token)
      .then(res => {
        if (!res.success) {
          setError(res.error || '邀請連結無效');
          setInviteStatus('expired');
        } else {
          setInviteStatus(res.status || 'pending');
          setQuestionText(res.question_text || '');
          setQuestionId(res.question_id || '');
          setInitiatorName(res.initiator_name || '');
          if (res.result_output) setResult(res.result_output);
        }
      })
      .catch(() => {
        setError('無法載入邀請資訊');
        setInviteStatus('expired');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (!name.trim()) { setSubmitError('請輸入姓名或代稱'); return; }
    if (!year || year.length !== 4) { setSubmitError('請輸入正確的出生年份（4位數）'); return; }
    if (!month || parseInt(month) < 1 || parseInt(month) > 12) { setSubmitError('請輸入正確的出生月份（1-12）'); return; }
    if (!day || parseInt(day) < 1 || parseInt(day) > 31) { setSubmitError('請輸入正確的出生日期（1-31）'); return; }
    if (!hour) { setSubmitError('請選擇出生時辰'); return; }
    setSubmitError('');
    setSubmitting(true);
    try {
      const birth_time = buildBirthTime(year, month, day, hour);
      const res = await submitInvite(token!, name.trim(), birth_time, gender);
      if (res.success && res.result_output) {
        setResult(res.result_output);
        setSubmitted(true);
        setInviteStatus('completed');
        if (res.invitee_name) setName(res.invitee_name);
        if (res.initiator_name) setInitiatorName(res.initiator_name);
      } else {
        setSubmitError(res.error || '分析失敗，請稍後再試');
      }
    } catch (err: any) {
      setSubmitError(err.message || '提交失敗，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* 標題 */}
      <Box textAlign="center" mb={3}>
        <AutoAwesomeIcon sx={{ fontSize: 48, color: '#667eea', mb: 1 }} />
        <Typography variant="h4" fontWeight="bold" sx={{ color: '#667eea' }}>
          我的好運氣 配對分析
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          紫微斗數命盤配對邀請
        </Typography>
      </Box>

      {/* 邀請無效或過期 */}
      {inviteStatus === 'expired' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || '此邀請連結已過期或無效，請聯繫邀請您的人重新產生'}
        </Alert>
      )}

      {/* 問題資訊 */}
      {(inviteStatus === 'pending' || inviteStatus === 'completed') && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea22, #764ba222)' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            分析問題
          </Typography>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {questionText}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
            <Chip
              label={`邀請人：${initiatorName}`}
              size="small"
              sx={{ bgcolor: '#667eea', color: 'white' }}
            />
            <Chip
              label={questionId.substring(0, questionId.length - 3)}
              size="small"
              variant="outlined"
              color="secondary"
            />
          </Box>
        </Paper>
      )}

      {/* 已完成：顯示結果 */}
      {inviteStatus === 'completed' && result && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#667eea' }}>
            配對分析結果
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <ReportContent text={result} />
        </Paper>
      )}

      {/* 待填寫：顯示表單 */}
      {inviteStatus === 'pending' && !submitted && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            請輸入您的出生資料
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {initiatorName} 邀請您進行命盤配對分析，請填寫您的基本出生資訊
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>
          )}

          <Grid container spacing={2}>
            {/* 姓名 */}
            <Grid item xs={12}>
              <TextField
                label="姓名或代稱"
                fullWidth
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="您的姓名或暱稱"
              />
            </Grid>

            {/* 性別 */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>性別</InputLabel>
                <Select
                  value={gender}
                  label="性別"
                  onChange={(e: SelectChangeEvent) => setGender(e.target.value as 'Male' | 'Female')}
                >
                  <MenuItem value="Male">男</MenuItem>
                  <MenuItem value="Female">女</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* 出生年 */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="出生年"
                fullWidth
                value={year}
                onChange={e => setYear(e.target.value)}
                placeholder="例：1990"
                inputProps={{ maxLength: 4 }}
              />
            </Grid>

            {/* 出生月 */}
            <Grid item xs={6} sm={2}>
              <TextField
                label="月"
                fullWidth
                value={month}
                onChange={e => setMonth(e.target.value)}
                placeholder="1-12"
                inputProps={{ maxLength: 2 }}
              />
            </Grid>

            {/* 出生日 */}
            <Grid item xs={6} sm={2}>
              <TextField
                label="日"
                fullWidth
                value={day}
                onChange={e => setDay(e.target.value)}
                placeholder="1-31"
                inputProps={{ maxLength: 2 }}
              />
            </Grid>

            {/* 出生時辰 */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>出生時辰</InputLabel>
                <Select
                  value={hour}
                  label="出生時辰"
                  onChange={(e: SelectChangeEvent) => setHour(e.target.value)}
                >
                  {hourOptions.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {(hour === '23_early' || hour === '23_late') && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {hour === '23_early'
                    ? '子時凌晨（00:01–01:00）：請填寫您實際出生的那一天日期'
                    : '子時深夜（23:01–24:00）：請填寫您實際出生的那一天日期'}
                </Alert>
              )}
            </Grid>
          </Grid>

          <Box mt={3}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleSubmit}
              disabled={submitting}
              sx={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                py: 1.5,
                fontSize: '1rem',
              }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : '開始配對分析'}
            </Button>
          </Box>
        </Paper>
      )}

      {/* 版權 */}
      <Box textAlign="center" mt={3}>
        <Typography variant="caption" color="text.disabled">
          Powered by 我的好運氣 · howyun.me
        </Typography>
      </Box>
    </Container>
  );
};

export default InvitePage;
